import {
  enableProdMode,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { EMPTY } from 'rxjs';

import { environment } from './environments/environment';
import * as CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import { AppComponent } from './app/app.component';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_ROUTES } from './app/app.routes';
import { bootstrapApplication } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { InterceptorService } from './app/interceptors/interceptor.service';
import {
  HTTP_INTERCEPTORS,
  withInterceptorsFromDi,
  provideHttpClient,
  withXhr,
} from '@angular/common/http';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import {
  provideTranslateService,
  provideTranslateLoader,
} from '@ngx-translate/core';
import { AppTranslateLoader } from './app/shared/services/translate.loader';
import { DEFAULT_LANGUAGE } from './app/shared/services/language.service';
import { storageOfflineServiceProvider } from './app/shared/db/StorageProvider';
import { IonicRouteStrategy, IonicModule } from '@ionic/angular';
import {
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { RoleAwarePreloadingStrategy } from './app/shared/services/role-aware-preloading.strategy';

// Datos de fecha en español para LocalDatePipe (en-US viene incluido).
registerLocaleData(localeEs);

if (environment.production) {
  enableProdMode();
}

// Detectar contexto nativo (Cordova / Capacitor) en tiempo de carga del módulo.
// En browser/PWA: window.cordova es undefined y Capacitor.isNative es false.
const isNativeContext =
  typeof (window as any).cordova !== 'undefined' ||
  !!(window as any).Capacitor?.isNative;

// El SW solo debe activarse en producción real (dominio externo).
// environment.production no es confiable porque environment.ts ya trae production:true
// para que `npm run build` funcione sin configuración extra.
// Detectar localhost es más robusto: el SW no tiene ngsw-worker.js en dev server.
const isLocalDev = ['localhost', '127.0.0.1'].includes(
  window.location.hostname,
);

// El plugin Cordova Network usa herencia ES5 sobre una clase ES6 base.
// Instanciarlo en un contexto browser (ionic serve / PWA) lanza:
//   "Class constructor AwesomeCordovaNativePlugin cannot be invoked without 'new'"
// Solución: proveer un stub sin-op en browser y el plugin real solo en nativo.
const networkProvider = isNativeContext
  ? Network
  : {
      provide: Network,
      useValue: {
        type: 'unknown',
        onDisconnect: () => EMPTY,
        onConnect: () => EMPTY,
      },
    };

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(APP_ROUTES, withPreloading(RoleAwarePreloadingStrategy)),
    importProvidersFrom(
      IonicModule.forRoot(),
      IonicStorageModule.forRoot({
        name: '__levemarketdb',
        driverOrder: [
          CordovaSQLiteDriver._driver,
          Drivers.IndexedDB,
          Drivers.LocalStorage,
        ],
      }),
    ),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    storageOfflineServiceProvider,
    networkProvider,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true,
    },
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // i18n: inglés (en-US) por defecto; el idioma persistido se aplica en
    // AppComponent tras platform.ready() (LanguageService.applyLanguage).
    provideTranslateService({
      lang: DEFAULT_LANGUAGE,
      fallbackLang: DEFAULT_LANGUAGE,
      loader: provideTranslateLoader(AppTranslateLoader),
    }),
    // ngsw-worker.js solo existe en el build de producción.
    // Se deshabilita en localhost (ionic serve) y en apps Capacitor nativas.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isLocalDev && !isNativeContext,
      registrationStrategy: 'registerImmediately',
    }),
  ],
}).catch((err) => console.log(err));
